/// <amd-module name="TFS/Admin/Groups/Pivots/Permissions/Redux/PermissionsPivotSaga" />
import { effects, SagaIterator } from "redux-saga";
import { ActionsOfType, ActionWithPayload } from "redux/Utils/ActionHelper";
import { PermissionValue, SubjectPermissions } from "TFS/Admin/Common/Contracts/OrganizationSettings";
import { AccessControlEntry, AceExtendedInformation } from "TFS/Admin/Common/Contracts/Security";
import { ISecurityRestClient, SecurityClientName } from "TFS/Admin/Common/RestClient/Security";
import { PermissionsOperation } from "TFS/Admin/Groups/Common/Constants";
import { GroupsViewActions } from "TFS/Admin/Groups/Common/Redux/Actions/GroupsViewActions";
import * as Resources from "TFS/Admin/Groups/Common/Resources";
import { GroupsComponent, GroupsTelemetryLogger } from "TFS/Admin/Groups/Common/Util/GroupsTelemetryLogger";
import * as PermissionsDataSource from "TFS/Admin/Groups/Pivots/Permissions/DataSource/PermissionsPivotDataSource";
import * as PermissionsTraceSource from "TFS/Admin/Groups/Pivots/Permissions/DataSource/PermissionsTraceDataSource";
import { PermissionsPivotActions, PermissionsPivotActionTypes } from "TFS/Admin/Groups/Pivots/Permissions/Redux/PermissionsPivotActions";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IdentityDescriptor } from "VSS/Platform/Contracts/Identities";
import { MessageCardSeverity } from "VSSUI/MessageCard";

/**
 * Root work item saga.
 */
export function* PermissionsPivotSaga(): SagaIterator {
    // Register the initialize Users handler
    yield effects.takeEvery(PermissionsPivotActionTypes.InitView, initializePermissionsPivot);
    yield effects.takeEvery(PermissionsPivotActionTypes.TracePermissions, tracePermissions);
    yield effects.takeEvery(PermissionsPivotActionTypes.SetPermission, setPermission);
    yield effects.takeEvery(PermissionsPivotActionTypes.RemovePermission, removePermission);
}

export function* initializePermissionsPivot(action: ActionsOfType<PermissionsPivotActions, PermissionsPivotActionTypes.InitView>): SagaIterator {
    const { subjectDescriptor, subjectName } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    var telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(
        pageContext,
        GroupsComponent.PermissionsPivot,
        "Fetch subject permissions"
    );
    yield effects.call(telemetryLogger.captureAttemptStart);

    try {
        if (subjectDescriptor && subjectDescriptor.length > 0 && pageContext) {
            const permissions: SubjectPermissions = (yield effects.call(PermissionsDataSource.getPermissions, pageContext, subjectDescriptor)) as any;

            if (permissions) {
                yield effects.put(PermissionsPivotActions.initialPermissionsLoadSucceeded(permissions));
                yield effects.put(GroupsViewActions.setPermissionsMarkTTI());
                yield effects.put(PermissionsPivotActions.tracePermissions(subjectDescriptor, permissions));
                return;
            } else {
                yield effects.put(PermissionsPivotActions.initialPermissionsLoadFailed());
                yield effects.call(telemetryLogger.captureError, "No permissions returned");
            }
        } else {
            yield effects.put(PermissionsPivotActions.initialPermissionsLoadFailed());
            yield effects.call(telemetryLogger.captureError, "No subjectDescriptor");
        }
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);

        yield effects.put(PermissionsPivotActions.initialPermissionsLoadFailed());
    }

    yield effects.put(
        PermissionsPivotActions.permissionOperationStatus(
            PermissionsOperation.Fetch,
            format(Resources.FetchPermissionsFailed, subjectName),
            MessageCardSeverity.Error
        )
    );
}

export function* setPermission(action: ActionsOfType<PermissionsPivotActions, PermissionsPivotActionTypes.SetPermission>): SagaIterator {
    const identityDescriptor: IdentityDescriptor = action.payload.identityDescriptor;
    const { namespaceId, token, permissionValue, permissionBit } = action.payload.updates;
    const isProjectScope = action.payload.isProjectScope;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    var telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.PermissionsPivot, "Set permissions");
    yield effects.call(telemetryLogger.captureAttemptStart);

    const securityClient: ISecurityRestClient = (yield effects.call([pageContext, pageContext.getRestClient], SecurityClientName)) as any;

    var allow: number = 0;
    var deny: number = 0;

    if (permissionValue == PermissionValue.Allow) {
        allow = permissionBit;
    } else if (permissionValue == PermissionValue.Deny) {
        deny = permissionBit;
    }

    const aceExtendedInfo: AceExtendedInformation = {
        effectiveAllow: allow,
        effectiveDeny: deny,
        inheritedAllow: allow,
        inheritedDeny: deny
    };

    const setAccessControlEntriesItem: AccessControlEntry = {
        descriptor: identityDescriptor,
        allow: allow,
        deny: deny,
        extendedInfo: aceExtendedInfo
    };

    const container = {
        token: token,
        merge: true,
        accessControlEntries: [setAccessControlEntriesItem]
    };

    try {
        yield effects.call([securityClient, securityClient.setAccessControlEntries], container, namespaceId);

        yield effects.put(
            PermissionsPivotActions.updatePermission(namespaceId, token, permissionBit, permissionValue, undefined, undefined, isProjectScope)
        );
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);

        yield effects.put(PermissionsPivotActions.updatePermissionFailed(namespaceId, permissionBit));
    }
}

export function* removePermission(action: ActionsOfType<PermissionsPivotActions, PermissionsPivotActionTypes.RemovePermission>): SagaIterator {
    const identityDescriptor: string = action.payload.identityDescriptor;
    const subjectDescriptor: string = action.payload.subjectDescriptor;
    const namespaceId: string = action.payload.namespaceId;
    const permissionBit: number = action.payload.permissionsBit;
    const token: string = action.payload.token;
    const isProjectScope = action.payload.isProjectScope;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    var telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.PermissionsPivot, "Remove permissions");
    yield effects.call(telemetryLogger.captureAttemptStart);

    const securityClient: ISecurityRestClient = (yield effects.call([pageContext, pageContext.getRestClient], SecurityClientName)) as any;

    try {
        yield effects.call([securityClient, securityClient.removePermission], namespaceId, identityDescriptor, permissionBit, token);

        // Nothing about the data returned from removePermissions tells us whether "not set" results in allow inherited or deny inherited, etc.
        // So we have no choice but to fetch the set again and see what we have.
        const permissions: SubjectPermissions = (yield effects.call(PermissionsDataSource.getPermissions, pageContext, subjectDescriptor)) as any;

        var newValue: PermissionValue = PermissionValue.NotSet;
        for (var i = 0; i < permissions.subjectPermissions.length; i++) {
            if (
                permissions.subjectPermissions[i].namespaceId == namespaceId &&
                permissions.subjectPermissions[i].token == token &&
                permissions.subjectPermissions[i].bit == permissionBit
            ) {
                newValue = permissions.subjectPermissions[i].effectivePermissionValue;

                yield effects.put(
                    PermissionsPivotActions.updatePermission(
                        namespaceId,
                        token,
                        permissionBit,
                        newValue,
                        permissions.subjectPermissions[i].isPermissionInherited,
                        permissions.subjectPermissions[i].isPermissionGrantedBySystem,
                        isProjectScope
                    )
                );

                if (permissions.subjectPermissions[i].isPermissionInherited) {
                    let permissionTrace = (yield effects.call(
                        PermissionsTraceSource.tracePermissions,
                        pageContext,
                        subjectDescriptor,
                        namespaceId,
                        token,
                        permissionBit.toString()
                    )) as any;

                    yield effects.put(
                        PermissionsPivotActions.updateSingleTracePermissionSucceeded(permissions.subjectPermissions[i], permissionTrace)
                    );
                }
            }
        }
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);

        yield effects.put(PermissionsPivotActions.updatePermissionFailed(namespaceId, permissionBit));
    }
}

export function* tracePermissions(
    action: ActionWithPayload<PermissionsPivotActionTypes.TracePermissions, { subjectDescriptor: string; permissions: SubjectPermissions }>
): SagaIterator {
    const { subjectDescriptor, permissions } = action.payload;
    const inheritedPermissions = permissions.subjectPermissions.filter(permission => permission.isPermissionInherited);
    if (!subjectDescriptor || subjectDescriptor.length == 0 || inheritedPermissions.length === 0) {
        return;
    }

    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    var telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.PermissionsPivot, "Fetch permission traces");
    yield effects.call(telemetryLogger.captureAttemptStart);

    try {
        const responses = (yield effects.all(
            inheritedPermissions.map(permission =>
                effects.call(
                    PermissionsTraceSource.tracePermissions,
                    pageContext,
                    subjectDescriptor,
                    permission.namespaceId,
                    permission.token,
                    permission.bit.toString()
                )
            )
        )) as any;

        yield effects.put(PermissionsPivotActions.tracePermissionsSucceeded(inheritedPermissions, responses));
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);
        yield effects.put(PermissionsPivotActions.tracePermissionsFailed());
    }
}
